import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Modal,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import HeroSlider from "../components/HeroSlider/HeroSlider";
import {
  getAllBerita,
  getRekapLembaga,
  getClients,
  getUploadUrl,
  BASE_URL,
} from "../services/api";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage, pickLang } from "../contexts/LanguageContext";
import { prepareRichContentHtml } from "../utils/quillContentSetup";
import { getBeritaDetailPath } from "../utils/beritaSlug";
import { normalizeContentImageUrls } from "../services/api";
import "./Home.css";

const Home = () => {
  const { loading: themeLoading } = useTheme();
  const { language } = useLanguage();
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [welcomeData, setWelcomeData] = useState({
    judul: "Selamat Datang",
    judul_en: "Welcome",
    isiberita:
      "LP3H  adalah lembaga yang berkomitmen dalam pengawasan dan penjaminan produk halal di Indonesia.",
    isiberita_en: "",
  });
  const [beritaList, setBeritaList] = useState([]);
  const [layananList, setLayananList] = useState([]);
  const [rekapData, setRekapData] = useState(null);
  const [loadingRekap, setLoadingRekap] = useState(false);
  const [clientList, setClientList] = useState([]);
  const [showAllClientsModal, setShowAllClientsModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    fetchWelcomeData();
    fetchBerita();
    fetchLayanan();
    fetchRekapLembaga();
    fetchClients();
    // Hide skeleton after 1.5 seconds or when theme and data are both ready
    const timer = setTimeout(() => setShowSkeleton(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  const fetchClients = async () => {
    try {
      const response = await getClients();
      if (response.data.success) {
        setClientList(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchRekapLembaga = async () => {
    try {
      setLoadingRekap(true);
      const response = await getRekapLembaga();
      if (response.data && response.data.data) {
        setRekapData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching rekap lembaga:", error);
    } finally {
      setLoadingRekap(false);
    }
  };

  const fetchWelcomeData = async () => {
    try {
      const response = await getAllBerita();
      if (response.data.success) {
        const welcome = response.data.data.find(
          (item) => item.jenisberita === "welcome",
        );
        if (welcome) {
          setWelcomeData(welcome);
        }
      }
    } catch (error) {
      console.error("Error fetching welcome data:", error);
    }
  };

  const fetchBerita = async () => {
    try {
      const response = await getAllBerita();
      if (response.data.success) {
        // Filter berita only (not welcome), take latest 3
        const berita = response.data.data
          .filter((item) => item.jenisberita === "berita")
          .slice(0, 3);
        console.log("Berita fetched:", berita.length, berita);
        setBeritaList(berita);
      }
    } catch (error) {
      console.error("Error fetching berita:", error);
    }
  };

  const fetchLayanan = async () => {
    try {
      const response = await getAllBerita();
      if (response.data.success) {
        // Filter semua kecuali berita dan welcome (anggap sisanya adalah layanan)
        const layanan = response.data.data.filter(
          (item) =>
            item.jenisberita !== "berita" && item.jenisberita !== "welcome",
        );
        // Sort by nourut (ascending)
        const sortedLayanan = layanan.sort((a, b) => {
          const numA = parseInt(a.nourut) || 999;
          const numB = parseInt(b.nourut) || 999;
          return numA - numB;
        });
        console.log("Layanan data:", sortedLayanan);
        setLayananList(sortedLayanan);
      }
    } catch (error) {
      console.error("Error fetching layanan:", error);
    }
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("id-ID", options);
  };

  const stripHtml = (html) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const clientsByUpdate = useMemo(() => {
    return [...clientList].sort((a, b) => {
      const dateA = new Date(a.tglupdate || 0).getTime();
      const dateB = new Date(b.tglupdate || 0).getTime();
      return dateB - dateA;
    });
  }, [clientList]);

  const topClients = useMemo(
    () => clientsByUpdate.slice(0, 6),
    [clientsByUpdate],
  );

  const welcomeHtml = useMemo(() => {
    const raw = pickLang(welcomeData, "isiberita", language);
    return prepareRichContentHtml(normalizeContentImageUrls(raw || ""));
  }, [welcomeData, language]);

  const handleOpenClientDetail = (client) => {
    setSelectedClient(client);
  };

  const handleCloseClientDetail = () => {
    setSelectedClient(null);
  };

  return (
    <div className="home-page">
      <HeroSlider />

      {/* Welcome Section */}
      <section className="welcome-section py-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={10} lg={9}>
              <div className="fade-in-up">
                <h2 className="section-title text-center mb-4">
                  {pickLang(welcomeData, "judul", language)}
                </h2>
                <div
                  className="lead welcome-content"
                  dangerouslySetInnerHTML={{ __html: welcomeHtml }}
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Features Section — Layanan Kami */}
      <section className="features-section py-5">
        <Container>
          <div className="text-center mb-5 fade-in-up">
            <h2 className="section-title">
              {language === "en" ? "Our Services" : "Layanan Kami"}
            </h2>
            <p className="section-subtitle">
              {language === "en"
                ? "Our commitment to ensuring quality halal products"
                : "Komitmen kami dalam menjamin produk halal berkualitas"}
            </p>
          </div>
          <Row className="g-4">
            {showSkeleton || themeLoading ? (
              // Show skeleton loading while theme is loading or on initial load
              [...Array(4)].map((_, idx) => (
                <Col md={6} lg={3} key={idx}>
                  <div className="feature-box" style={{ opacity: 0.6 }}>
                    <div
                      className="feature-icon"
                      style={{
                        background:
                          "linear-gradient(90deg, #e2e8f0 25%, #f8fafc 50%, #e2e8f0 75%)",
                        backgroundSize: "200% 100%",
                        animation: "loading 1.5s infinite",
                      }}
                    />
                    <div
                      style={{
                        height: "1.5rem",
                        background: "#e2e8f0",
                        borderRadius: "8px",
                        marginBottom: "1rem",
                      }}
                    />
                    <div
                      style={{
                        height: "2.5rem",
                        background: "#e2e8f0",
                        borderRadius: "8px",
                      }}
                    />
                  </div>
                </Col>
              ))
            ) : layananList.length === 0 ? (
              <Col xs={12}>
                <div className="text-center py-5">
                  <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                  <p className="text-muted">
                    {language === "en"
                      ? "No services available yet"
                      : "Belum ada layanan tersedia"}
                  </p>
                </div>
              </Col>
            ) : (
              // Dynamic cards from database
              layananList.map((layanan, index) => {
                // Icon mapping berdasarkan jenisberita - default icon jika tidak sesuai
                const iconMap = {
                  sertifikasi: "fas fa-certificate",
                  pendampingan: "fas fa-user-check",
                  edukasi: "fas fa-book-open",
                  konsultasi: "fas fa-headset",
                };

                return (
                  <Col md={6} lg={3} key={layanan.id}>
                    <Link
                      to={`/layanan/${layanan.id}`}
                      style={{ textDecoration: "none", color: "inherit" }}
                    >
                      <div
                        className="feature-box fade-in-up"
                        style={{ animationDelay: `${(index + 1) * 0.1}s` }}
                      >
                        <div
                          className="feature-icon"
                          style={{
                            background: `linear-gradient(135deg, var(--primary-color, #10b981) 0%, var(--primary-hover, #059669) 100%)`,
                            boxShadow: `0 8px 24px rgba(var(--primary-rgb, 16, 185, 129), 0.3)`,
                          }}
                          title={`Icon: ${layanan.foto ? "Image" : "Icon"}`}
                        >
                          {layanan.foto ? (
                            <img
                              src={`${BASE_URL}/uploads/${layanan.foto}`}
                              alt={pickLang(layanan, "judul", language)}
                              className="feature-icon-img"
                            />
                          ) : (
                            <i
                              className={
                                iconMap[layanan.jenisberita] || "fas fa-cogs"
                              }
                            ></i>
                          )}
                        </div>
                        <h4>{pickLang(layanan, "judul", language)}</h4>
                        <div
                          dangerouslySetInnerHTML={{
                            __html:
                              pickLang(layanan, "ringkasan", language) ||
                              stripHtml(
                                pickLang(layanan, "isiberita", language),
                              ).substring(0, 80) + "...",
                          }}
                        />
                      </div>
                    </Link>
                  </Col>
                );
              })
            )}
          </Row>
        </Container>
      </section>

      {/* Client Section — only shown when records exist */}
      {clientList.length > 0 && (
        <section className="clients-section py-5">
          <Container>
            <div className="text-center mb-5 fade-in-up">
              <h2 className="section-title">
                {language === "en" ? "Our Clients" : "Klien Kami"}
              </h2>
              <p className="section-subtitle">
                {language === "en"
                  ? "Trusted by organizations across Indonesia"
                  : "Dipercaya oleh berbagai organisasi di Indonesia"}
              </p>
            </div>
            <Row className="g-4 justify-content-center">
              {topClients.map((client) => (
                <Col md={6} lg={4} key={client.id}>
                  <Card className="client-card h-100">
                    <Card.Body>
                      {client.logo && (
                        <div className="client-card-logo">
                          <img
                            src={getUploadUrl(client.logo)}
                            alt={client.nama}
                          />
                        </div>
                      )}
                      {client.foto && (
                        <div className="client-card-photo">
                          <img
                            src={getUploadUrl(client.foto)}
                            alt={client.nama}
                          />
                        </div>
                      )}
                      <h4 className="client-card-name">{client.nama}</h4>
                      {client.testimoni && (
                        <blockquote className="client-card-testimoni">
                          <i className="fas fa-quote-left me-2"></i>
                          {client.testimoni}
                        </blockquote>
                      )}
                      {client.alamat && (
                        <p className="client-card-alamat">
                          <i className="fas fa-map-marker-alt me-2"></i>
                          {client.alamat}
                        </p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
            <div className="text-center mt-5">
              <Button
                variant="outline-primary"
                size="lg"
                className="client-view-all-btn"
                onClick={() => setShowAllClientsModal(true)}
              >
                {language === "en" ? "View All" : "Lihat Semua"}
                <i className="fas fa-arrow-right ms-2"></i>
              </Button>
            </div>
          </Container>
        </section>
      )}

      {/* Modal: Daftar Semua Client */}
      <Modal
        show={showAllClientsModal}
        onHide={() => setShowAllClientsModal(false)}
        size="lg"
        centered
        scrollable
        className="client-list-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {language === "en" ? "All Clients" : "Daftar Semua Client"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="client-list-grid">
            {clientsByUpdate.map((client) => (
              <div key={client.id} className="client-list-item">
                <div className="client-list-item-logo">
                  {client.logo ? (
                    <img src={getUploadUrl(client.logo)} alt={client.nama} />
                  ) : (
                    <div className="client-list-item-placeholder">
                      <i className="fas fa-building"></i>
                    </div>
                  )}
                </div>
                <div className="client-list-item-info">
                  <h5>{client.nama}</h5>
                </div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleOpenClientDetail(client)}
                >
                  {language === "en" ? "View Detail" : "Lihat Detail"}
                </Button>
              </div>
            ))}
          </div>
        </Modal.Body>
      </Modal>

      {/* Modal: Detail Client */}
      <Modal
        show={!!selectedClient}
        onHide={handleCloseClientDetail}
        size="lg"
        centered
        className="client-detail-modal"
      >
        {selectedClient && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>{selectedClient.nama}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {selectedClient.logo && (
                <div className="client-detail-logo">
                  <img
                    src={getUploadUrl(selectedClient.logo)}
                    alt={selectedClient.nama}
                  />
                </div>
              )}
              {selectedClient.testimoni && (
                <blockquote className="client-detail-testimoni">
                  <i className="fas fa-quote-left me-2"></i>
                  {selectedClient.testimoni}
                </blockquote>
              )}
              {selectedClient.foto && (
                <div className="client-detail-photo">
                  <img
                    src={getUploadUrl(selectedClient.foto)}
                    alt={selectedClient.nama}
                  />
                </div>
              )}
            </Modal.Body>
          </>
        )}
      </Modal>

      {/* Statistics Section */}
      <section className="stats-section py-5" style={{ display: "none" }}>
        <Container>
          {loadingRekap && (
            <Row className="justify-content-center mb-4">
              <Col xs="auto">
                <div className="stats-loading text-center">
                  <Spinner animation="border" role="status" variant="light">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              </Col>
            </Row>
          )}
          <Row className="text-center g-4">
            <Col sm={6} lg={3}>
              <div className="stat-box fade-in-up">
                <div className="stat-number">
                  {rekapData
                    ? rekapData.jumlah_sertifikat.toLocaleString("id-ID")
                    : "—"}
                </div>
                <div className="stat-label">
                  {language === "en"
                    ? "Issued Certificates"
                    : "Sertifikat Terbit"}
                </div>
              </div>
            </Col>
            <Col sm={6} lg={3}>
              <div
                className="stat-box fade-in-up"
                style={{ animationDelay: "0.1s" }}
              >
                <div className="stat-number">
                  {rekapData
                    ? rekapData.jumlah_produk.toLocaleString("id-ID")
                    : "—"}
                </div>
                <div className="stat-label">
                  {language === "en" ? "Total Products" : "Jumlah Produk"}
                </div>
              </div>
            </Col>
            <Col sm={6} lg={3}>
              <div
                className="stat-box fade-in-up"
                style={{ animationDelay: "0.2s" }}
              >
                <div className="stat-number">
                  {rekapData
                    ? rekapData.jumlah_pelaku_usaha.toLocaleString("id-ID")
                    : "—"}
                </div>
                <div className="stat-label">
                  {language === "en" ? "Business Actors" : "Pelaku Usaha"}
                </div>
              </div>
            </Col>
            <Col sm={6} lg={3}>
              <div
                className="stat-box fade-in-up"
                style={{ animationDelay: "0.3s" }}
              >
                <div className="stat-number">
                  {rekapData
                    ? rekapData.jumlah_pendamping.toLocaleString("id-ID")
                    : "—"}
                </div>
                <div className="stat-label">
                  {language === "en" ? "P3H Companions" : "Pendamping P3H"}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Berita Section */}
      <section className="berita-section py-5 bg-light">
        <Container>
          <div className="text-center mb-5">
            <h2 className="section-title">
              {language === "en" ? "Latest News" : "Berita Terbaru"}
            </h2>
          </div>
          <Row className="g-4">
            {beritaList.length === 0 ? (
              <Col xs={12}>
                <div className="empty-state">
                  <i className="fas fa-newspaper"></i>
                  <p>
                    {language === "en"
                      ? "No news available yet"
                      : "Belum ada berita tersedia"}
                  </p>
                </div>
              </Col>
            ) : (
              beritaList.map((berita, index) => (
                <Col md={6} lg={4} key={berita.id}>
                  <Card
                    className="berita-card h-100"
                    style={{
                      animationDelay: `${index * 0.1}s`,
                      opacity: 1,
                    }}
                  >
                    {berita.foto && (
                      <div className="berita-card-image">
                        <Card.Img
                          variant="top"
                          src={`${BASE_URL}/uploads/${berita.foto}`}
                          alt={pickLang(berita, "judul", language)}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                        <div className="berita-overlay">
                          <i className="fas fa-eye"></i>
                        </div>
                      </div>
                    )}
                    <Card.Body className="d-flex flex-column">
                      <div className="berita-date mb-2">
                        <i className="fas fa-calendar-alt me-2"></i>
                        {formatDate(berita.tanggal)}
                      </div>
                      <Card.Title className="berita-title">
                        {pickLang(berita, "judul", language)}
                      </Card.Title>
                      <Card.Text className="berita-excerpt flex-grow-1">
                        {pickLang(berita, "ringkasan", language) ||
                          stripHtml(
                            pickLang(berita, "isiberita", language),
                          ).substring(0, 150) + "..."}
                      </Card.Text>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        as={Link}
                        to={getBeritaDetailPath(
                          berita,
                          pickLang(berita, "judul", language),
                        )}
                        className="mt-auto berita-btn"
                      >
                        {language === "en" ? "Read More" : "Baca Selengkapnya"}{" "}
                        <i className="fas fa-arrow-right ms-1"></i>
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            )}
          </Row>
          {beritaList.length > 0 && (
            <div className="text-center mt-5">
              <Button
                as={Link}
                to="/berita"
                variant="primary"
                size="lg"
                className="view-all-btn"
              >
                {language === "en" ? "View All News" : "Lihat Semua Berita"}
                <i className="fas fa-chevron-right ms-2"></i>
              </Button>
            </div>
          )}
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5" style={{ display: "none" }}>
        <Container>
          <Row className="justify-content-center">
            <Col md={10} lg={8} className="text-center">
              <div className="cta-content fade-in-up">
                <div className="cta-icon mb-4">
                  <i className="fas fa-user-plus"></i>
                </div>
                <h2 className="text-white mb-3">
                  {language === "en"
                    ? "Register Now"
                    : "Daftarkan Diri Anda Sekarang"}
                </h2>
                <p className="text-white mb-4 cta-description">
                  {language === "en"
                    ? "Join us as a Halal Product Process Companion (P3H) and contribute to ensuring halal products in Indonesia"
                    : "Bergabunglah dengan kami sebagai Pendamping Proses Produk Halal (P3H) dan berkontribusi dalam menjamin produk halal di Indonesia"}
                </p>
                <div className="cta-buttons">
                  <Button
                    as={Link}
                    to="/pendaftaran-p3h"
                    variant="light"
                    size="lg"
                    className="cta-primary-btn"
                  >
                    <i className="fas fa-user-plus me-2"></i>
                    {language === "en" ? "Register Now" : "Daftar Sekarang"}
                  </Button>
                  <Button
                    as={Link}
                    to="/tentang-kami"
                    variant="outline-light"
                    size="lg"
                    className="cta-secondary-btn ms-3"
                  >
                    <i className="fas fa-info-circle me-2"></i>
                    {language === "en" ? "Learn More" : "Pelajari Lebih Lanjut"}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
        <div className="cta-shape cta-shape-1"></div>
        <div className="cta-shape cta-shape-2"></div>
        <div className="cta-shape cta-shape-3"></div>
      </section>
    </div>
  );
};

export default Home;
